import {
  HttpClient,
  HttpHeaders,
  HttpParameterCodec,
  HttpParams
} from '@angular/common/http';
import {Router} from '@angular/router';
import {createStore} from 'devextreme-aspnet-data-nojquery';
import * as Sentry from '@sentry/angular-ivy';
import DataSource from 'devextreme/data/data_source';
import notify from 'devextreme/ui/notify';
import {StatusCodes} from 'http-status-codes';
import {BehaviorSubject, Observable} from 'rxjs';

import {LogOut} from '../../stores/actions/fusion-auth.actions';

class HttpSimpleEncodingCodec implements HttpParameterCodec {
  public encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  public encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  public decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  public decodeValue(value: string) {
    return decodeURIComponent(value);
  }
}

export class HttpRequestBuilder {
  private body?: object;
  private headers: HttpHeaders;
  private method: string = '';
  private parameters: HttpParams;
  private uri: string = '';
  private url: string = '';

  constructor(private http: HttpClient) {
    this.headers = new HttpHeaders();
    this.parameters = new HttpParams({encoder: new HttpSimpleEncodingCodec()});
  }

  /**
   * Adds a segment to the request uri
   */
  public addUriSegment(segment: string) {
    if (typeof this.uri !== 'string') {
      this.uri = '';
    }
    if (segment !== null && typeof segment !== 'undefined') {
      if (this.uri.charAt(this.uri.length - 1) !== '/') {
        this.uri = this.uri + '/';
      }
      this.uri = this.uri + segment;
    }
    return this;
  }

  /**
   * Get the full url + uri (if it exists)
   */
  public getFullUrl() {
    return this.url + (typeof this.uri === 'string' ? this.uri : '');
  }

  /**
   * Get the options object
   */
  public getOptions(withCredentials = true) {
    return {
      body: this.body,
      headers: this.headers,
      observe: 'response' as 'response',
      params: this.parameters,
      reportProgress: false,
      responseType: 'json' as 'json',
      withCredentials: withCredentials,
    };
  }

  /**
   * Adds a header to the request.
   *
   * @param name The name of the header.
   * @param value The value of the header.
   */
  public setHeader(name: string, value: string) {
    this.headers = this.headers.set(name, value);
    return this;
  }

  /**
   * Sets the body of the client request.
   *
   * @param body The object to be written to the request body as JSON.
   */
  public setJsonBody(body: any) {
    this.body = body;
    this.setHeader('Content-Type', 'application/json');
    // Omit the Content-Length, this is set by the browser. It is considered an un-safe header to set manually.
    return this;
  }

  /**
   * Sets the http method for the request
   */
  public setMethod(method: string) {
    this.method = method;
    return this;
  }

  /**
   * Sets the uri of the request
   */
  public setUri(uri: string) {
    this.uri = uri;
    return this;
  }

  /**
   * Sets the base url of the request.
   */
  public setUrl(url: string) {
    this.url = url;
    return this;
  }

  /**
   * Adds url parameters to the request.
   *
   * @param name The name of the parameter.
   * @param value The value of the URL parameter, may be a string, object or number.
   */
  public setUrlParameter(name: string, value: any) {
    if (value !== null && typeof value !== 'undefined') {
      this.parameters = this.parameters.set(name, value);
    }
    return this;
  }

  /**
   * Creates the HTTP request and returns an Observable that, when subscribed, will send the request to the server.
   */
  public build(withCredentials: boolean = true) {
    return this.http.request(this.method, this.getFullUrl(), this.getOptions(withCredentials));
  }
}

export const CustomStoreErrorHandler = (serviceInstance: any) => (event: any) => {
  Sentry.captureException(event);
  if (event.xhr.status === StatusCodes.GATEWAY_TIMEOUT || event.xhr.status === StatusCodes.FORBIDDEN || event.xhr.status === StatusCodes.UNAUTHORIZED) {
    notify('Your session has expired.', 'error');
    if (serviceInstance && serviceInstance.store) {
      serviceInstance.store.dispatch(new LogOut());
    }
  }
};

export interface SecureStoreOptions {
  dataSource: BehaviorSubject<DataSource>;
  routerService: Router;
  tokenRefresh: Observable<any>;
  loadParams: any;
  loadUrl: string;
  key?: string;
  headers?: object | null;
  onLoaded?: (response: any) => void;
}

export const SECURED_STORE = ({
                                dataSource,
                                routerService,
                                tokenRefresh,
                                loadParams,
                                loadUrl,
                                onLoaded,
                                key = 'id',
                                headers = null
                              }: SecureStoreOptions) => createStore({
  key,
  onBeforeSend: (operation, ajaxSettings) => {
    if (!ajaxSettings.headers) {
      ajaxSettings.headers = {};
    }
    ajaxSettings.headers.authorization = `JWT ${localStorage.getItem('authtoken')}`;
    if (headers) {
      ajaxSettings.headers = {
        ...ajaxSettings.headers,
        ...headers
      }
    }
  },
  errorHandler: (e) => {
    Sentry.captureException(e);
  },
  onAjaxError: ({xhr, error}) => {
    if (xhr.status === StatusCodes.GATEWAY_TIMEOUT || xhr.status === StatusCodes.FORBIDDEN || xhr.status === StatusCodes.UNAUTHORIZED) {
      if (localStorage.getItem('authtoken') && localStorage.getItem('authrefreshToken')) {
        tokenRefresh
          .subscribe(({body}) => {
            localStorage.setItem('authtoken', body.token);
            dataSource.value.load();
          }, e => {
            //Sentry.captureException(e);
            notify('Your session has expired.', 'error');
            routerService.navigateByUrl('/login').then();
          });
      } else {
        notify('Your session has expired.', 'error');
        routerService.navigateByUrl('/login').then();
      }
    }
  },
  onLoaded,
  loadUrl,
  loadParams,
});

